import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import Icon from './Icon';
import { livePhases } from '../phases';
import { fmtMoney } from '../money';
import { correctionNeedsApproval, type InvoiceLine } from '../projects';

/**
 * CORRECT A LINE'S CLASSIFICATION — change its capex/opex pool or its phase after
 * it was first set (line-correction governance, 2 Oct; screen 02). A reason is
 * always required. The tier banner tells the reader what will happen: a line not
 * yet paid is corrected at once (self-serve); a PAID line's correction is sent to
 * an admin or manager for approval. Shared by the ledger row's ⋯ menu and the open line.
 */
export function CorrectionDialog({
  projectId,
  line,
  onClose,
}: {
  projectId: string;
  line: InvoiceLine;
  onClose: () => void;
}) {
  const correctLine = useApp((s) => s.correctLine);
  const phases = useApp((s) => s.phases);
  const livePh = livePhases(phases);
  // Billable milestones are the only payment points a line can claim against.
  const billableMilestones = useApp((s) => s.milestones).filter((m) => m.billable);

  const [pool, setPool] = useState<'capex' | 'opex'>(line.pool);
  const [phaseId, setPhaseId] = useState(line.phase ?? '');
  const [milestoneId, setMilestoneId] = useState(line.milestone ?? '');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  // Escape closes only this dialog, never the budget modal behind it. Capture
  // phase + stopPropagation beats the modal's own window Escape (app-wide nested-
  // overlay rule, as in CalendarPopover).
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [onClose]);

  const needsApproval = correctionNeedsApproval(line);
  const poolChanged = pool !== line.pool;
  const phaseChanged = phaseId !== (line.phase ?? '');
  const milestoneChanged = milestoneId !== (line.milestone ?? '');
  const changed = poolChanged || phaseChanged || milestoneChanged;
  const canSubmit = changed && !!reason.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setProblem('');
    // Each changed field is its own correction, sharing the one reason.
    let err = '';
    if (poolChanged) err = await correctLine(projectId, line.id, 'pool', pool, reason);
    if (!err && phaseChanged) err = await correctLine(projectId, line.id, 'phase', phaseId, reason);
    if (!err && milestoneChanged) err = await correctLine(projectId, line.id, 'milestone', milestoneId, reason);
    setBusy(false);
    if (err) { setProblem(err); return; }
    onClose();
  };

  // Portal to the body: this dialog is opened from inside the invoice drawer, whose
  // transform makes it a containing block — rendered inline, the overlay is trapped in
  // the 540px drawer and the dialog centres on the drawer (shoved right, cramped,
  // buttons clipped) instead of the screen. The portal centres it on the viewport.
  return createPortal(
    <div className="overlay" onClick={onClose}>
      <div className="modal cor-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><span className="modal-head-title">Reclassify this line</span></div>

        <div className="modal-body">
          <div className={`cor-tier ${needsApproval ? 'is-approval' : 'is-self'}`}>
            <Icon name={needsApproval ? 'shield' : 'info'} size={14} />
            <span>
              {needsApproval ? (
                <>This line is <b>paid</b>, so the reclassification goes to an admin or manager for approval before it takes effect.</>
              ) : (
                <>This applies right away and is recorded on the line.</>
              )}
            </span>
          </div>

          <div className="cor-row">
            <label className="cor-fld">
              <span className="af-l">Pool</span>
              <div className="bud-seg">
                <button aria-pressed={pool === 'capex'} className={pool === 'capex' ? 'on' : ''} onClick={() => setPool('capex')}>Capex</button>
                <button aria-pressed={pool === 'opex'} className={pool === 'opex' ? 'on' : ''} onClick={() => setPool('opex')}>Opex</button>
              </div>
            </label>

            <label className="cor-fld">
              <span className="af-l">Phase</span>
              <select className="input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
                <option value="">Unassigned</option>
                {livePh.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          </div>

          {billableMilestones.length > 0 && (
            <label className="cor-fld">
              <span className="af-l">Billed against</span>
              <select className="input" value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}>
                <option value="">Not against a milestone</option>
                {billableMilestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.billValue != null ? ` — ${fmtMoney(m.billValue)}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="cor-fld">
            <span className="af-l">Reason<span className="req">*</span></span>
            <textarea
              className="input"
              rows={3}
              value={reason}
              autoFocus
              aria-required
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this line is being reclassified"
            />
          </label>

          {problem && <p className="id-problem">{problem}</p>}
        </div>

        <div className="modal-actions id-reason-btns">
          <button className="btn btn-small" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-small" disabled={!canSubmit} onClick={submit}>
            {needsApproval ? 'Send for approval' : 'Reclassify'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
